import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SpacingValue } from '@frk/eds-components';
import { ViewModeService } from '@services/view-mode.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';

/**
 * Logger
 */
const logger = Logger.getLogger('ContentBlocksComponent');

export interface ContentBlocksContent {
  background?: string;
  sectionId?: string;
  name?: string;
  displayName?: string;
  items?: Array<any>;
  itemsTwo?: Array<any>;
  marginBottom?: string;
  verticalAlignment?: string;
  hasContainerWide?: boolean;
}

/**
 * The Content Blocks component can be used to display a selection of
 * simple content blocks such as:
 *
 * 'Title'
 * 'Intro Paragraph'
 * 'Paragraph'
 * 'Chart'
 *
 * Selector: `ft-content-blocks`
 *
 * Exported as: `ContentBlocksComponent`
 */
@Component({
  selector: 'ft-content-blocks',
  templateUrl: './content-blocks.component.html',
  styleUrls: ['./content-blocks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentBlocksComponent
  extends AbstractBaseComponent
  implements OnChanges, OnInit {
  hasContainerWide = false;
  trackImpression = false;
  divId = '';

  /**
   * Constructor
   */
  constructor(
    private route: ActivatedRoute,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Get content from document
   */
  get content() {
    return this.document?.getData<ContentBlocksContent>();
  }

  /**
   * Get bottom margin for the content block
   */
  get marginBottom(): SpacingValue {
    return this.document?.getData<ContentBlocksContent>()
      .marginBottom as SpacingValue;
  }

  /**
   * Read content blocks
   */
  get contentBlocks() {
    return this.content?.contentBlocks || [];
  }
  /**
   * Debug Mode toggle
   */
  debugMode: boolean;
  public contentBlocksMarginBottom: string;

  /**
   * On Init
   */
  ngOnInit() {
    this.route?.queryParams?.subscribe((params) => {
      logger.debug(params);
      this.debugMode = params.padding === 'debug';
      logger.debug(this.debugMode);
    });
    this.hasContainerWide = this.isContainerWide();
    this.trackImpression = this.content?.enableImpressionTracking;
    this.divId = this.getDivId() ? this.getDivId() : '';

    /**
     * Content Blocks Component Level Margin
     */
    this.contentBlocksMarginBottom = this.component.getParameters().marginBottom;
  }

  determineSpacing(isLast?: boolean, index?: number): string {
    const individualMarginBottom =
      this.content?.contentBlocks[index].marginBottom ||
      this.content?.contentBlocks[index].spacingBottom;
    // Margin for - Content Blocks (parent container which will be holding children content block
    if (isLast) {
      if (
        individualMarginBottom === 'auto' ||
        individualMarginBottom === null ||
        individualMarginBottom === undefined
      ) {
        return 'xl';
      }
      return individualMarginBottom;
    } else if (this.removeMarginBottom !== 'true' && isLast) {
      return 'none';
    }
    // Margin for - Individual Content Block within Content Blocks
    return individualMarginBottom || 'none';
  }

  /**
   * gets container--wide setting from component
   */
  isContainerWide() {
    return this.component.getParameters()?.hasContainerWide;
  }

  /**
   * gets id from component if set
   */
  getDivId(): string {
    return this.component.getParameters()?.divId;
  }
}
